import * as React from 'react';
import {
    Box,
    Dialog,
    Stack,
    TablePagination,
    Tooltip,
    Typography,
} from '@mui/material';
import Filter from 'component/filter';
import colors from 'assets/theme/base/colors';
import borders from 'assets/theme/base/borders';
import FlexibleTable from 'component/flexible-table';
import { useEffect, useState } from 'react';
import { RowDataType, TableMetaData } from 'types/common/common';
import { useAppDispatch } from 'hooks/redux';
import { ReactComponent as Edit } from 'assets/images/staff-user/edit.svg';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import pxToRem from 'assets/theme/function/px-to-rem';
import AddImages from 'component/add-images';
import { StyledTableRow } from 'component/tabel-pagination-style';
import {
    AvatarSystemData,
    AvatarSystemListRequest,
} from 'types/avatar-system/avatar-system';
import FilterAvatarSystem from 'pages/avatar-systems/avatar-systems-filter';
import AddNewAvatar from 'pages/avatar-systems/add-new-avatar';
import EditAvatar from 'pages/avatar-systems/edit-avatar';
import {
    useAvatarImageUpdateMutation,
    useLazyAvatarListQuery,
    useAvatarCategoryQuery,
} from 'store/slices/cards/avatar/avatar';
import { API_ENDPOINT_URL } from 'constant';
import { openBackdrop } from 'store/slices/backdrop/backdrop-slice';
import SuccessSnackbarEffect from 'component/success-snackbar';
import { getStatusColor } from 'component/status-style';

export const initialListData: AvatarSystemListRequest = {
    page: 1,
    pageSize: 50,
    sort: 'createdAt',
};

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'img',
        position: 1,
        title: 'Image',
        isSortable: false,
    },
    {
        key: 'avatarName',
        position: 2,
        title: 'Name',
        isSortable: false,
    },
    {
        key: 'avatarCategory',
        position: 3,
        title: 'Avatar Type ',
        isSortable: false,
    },
    {
        key: 'productTitle',
        position: 4,
        title: 'Product Title ',
        isSortable: false,
    },

    {
        key: 'createdAt',
        position: 5,
        title: 'Creation Data',
        isSortable: true,
        type: 'date',
        format: 'DD.MM.YYYY HH:mm',
    },
    {
        key: 'status',
        position: 6,
        title: 'Status',
        isSortable: false,
    },

    {
        key: 'action',
        position: 7,
        title: 'Action',
        isSortable: false,
    },
];

const AvatarSystems: React.FC = () => {
    const [openAddAvatarDialog, setOpenAddAvatarDialog] = React.useState(false);
    const [tableMetaDataList, setTableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [openEditAvatarDialog, setOpenEditAvatarDialog] =
        React.useState(false);
    const [openAvatarImgDialog, setOpenAvatarImgDialog] = React.useState(false);
    const [dataAvatar, setDataAvatar] = React.useState<AvatarSystemData>();
    const [dataAvatarRequestList, setDataAvatarRequestList] =
        React.useState<AvatarSystemListRequest>(initialListData);
    const [pagesCount, setPagesCount] = useState<number>(0);
    const [page, setPage] = useState<number>(initialListData?.page);
    const [pageSize, setPageSize] = useState<number>(initialListData?.pageSize);
    const [sort, setSort] = useState<string>('createdAt');
    const [tableData, setTableData] = useState<RowDataType[]>([]);

    const { data: avatarCategory } = useAvatarCategoryQuery();

    const [
        avatarImagesUpdateApi,
        {
            error: imagesError,
            isError: imagesIsError,
            isSuccess: imagesIsSuccess,
        },
    ] = useAvatarImageUpdateMutation();

    const [getAvatarList, { data, isFetching }] = useLazyAvatarListQuery();

    const dispatch = useAppDispatch();

    const createNewAvatar = () => {
        setOpenAddAvatarDialog(true);
    };
    const updateImages = (images: FormData) => {
        avatarImagesUpdateApi({
            avatarId: dataAvatar?.id,
            image: images,
        });
    };

    const handelClickEditAvatar = (fileId: AvatarSystemData) => {
        setDataAvatar(fileId);
        setOpenEditAvatarDialog(true);
    };

    const openPictureDialogFunc = (params: AvatarSystemData) => {
        setDataAvatar(params);
        setOpenAvatarImgDialog(true);
    };

    const handleFilterData = (currentPage = 0) => {
        setPage(currentPage + 1);
    };

    const getAvatarListFunction = () => {
        getAvatarList(initialListData, false);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    useEffect(() => {
        setPagesCount(
            data && data?.data.length > 0 ? Number(data?.rowsCount) : 0,
        );
        const tableData: RowDataType[] = data
            ? data.data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      img: (
                          <>
                              {fullInfo?.haveFile ? (
                                  <Box
                                      component="img"
                                      src={`${API_ENDPOINT_URL}/avatar/file/${fullInfo.id}`}
                                      alt=""
                                      sx={{
                                          width: 60,
                                          height: 60,
                                          objectFit: 'cover',
                                          objectPosition: 'top',
                                          borderRadius:
                                              borders.borderRadius.section,
                                      }}
                                  />
                              ) : (
                                  <Typography variant="h5">-</Typography>
                              )}
                          </>
                      ),
                      status: (
                          <Typography
                              variant={'subtitle1'}
                              sx={{
                                  color: getStatusColor(fullInfo.status),
                              }}
                          >
                              {fullInfo.status}
                          </Typography>
                      ),
                      action: (
                          <>
                              <Box
                                  display={'flex'}
                                  justifyContent={'center'}
                                  gap={2}
                              >
                                  <Tooltip title="Edit">
                                      <Edit
                                          cursor="pointer"
                                          onClick={() =>
                                              handelClickEditAvatar({
                                                  id: fullInfo.id,
                                                  haveFile: fullInfo.haveFile,
                                              })
                                          }
                                      />
                                  </Tooltip>
                                  <Tooltip title="Add Photo">
                                      <AddPhotoAlternateIcon
                                          titleAccess={'Add photo'}
                                          cursor="pointer"
                                          onClick={() =>
                                              openPictureDialogFunc({
                                                  id: fullInfo.id,
                                                  haveFile: fullInfo.haveFile,
                                              })
                                          }
                                      />
                                  </Tooltip>
                              </Box>
                          </>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (dataAvatarRequestList) {
            getAvatarList({
                ...dataAvatarRequestList,
                sort: sort,
                page: page,
                pageSize: pageSize,
            });
        }
    }, [dataAvatarRequestList, page, pageSize, sort]);

    useEffect(() => {
        if (imagesIsSuccess) {
            setOpenAvatarImgDialog(false);
            getAvatarListFunction();
        }
    }, [imagesIsSuccess]);

    useEffect(() => {
        dispatch(openBackdrop({ open: isFetching }));
    }, [isFetching]);

    useEffect(() => {
        const error = imagesError;

        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [imagesIsError]);

    return (
        <Box>
            <Filter>
                <FilterAvatarSystem
                    setDataAvatarRequestList={setDataAvatarRequestList}
                    avatarCategory={avatarCategory}
                />
            </Filter>
            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(7)}
            >
                <FlexibleTable
                    tableMetaDataList={tableMetaDataList}
                    setTableMetaDataList={setTableMetaDataList}
                    tableDataList={tableData}
                    sort={sort}
                    setSort={setSort}
                    createNewFun={createNewAvatar}
                    buttonName={'Creat New Avatar'}
                />
                {pagesCount > 0 && (
                    <Stack spacing={2} alignItems={'end'}>
                        <TablePagination
                            rowsPerPageOptions={[50, 100, 150]}
                            component="div"
                            count={pagesCount}
                            rowsPerPage={pageSize}
                            page={page - 1}
                            labelRowsPerPage={'Number of rows'}
                            slotProps={{
                                select: {
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    MenuProps: {
                                        disablePortal: true,
                                        slotProps: {
                                            paper: {
                                                sx: {
                                                    backgroundColor:
                                                        colors.secondary.main,
                                                    padding: 0,
                                                },
                                            },
                                        },
                                    },
                                    native: false,
                                },
                            }}
                            onPageChange={(_, page) => handleFilterData(page)}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Stack>
                )}
            </Box>
            <Dialog
                open={openAddAvatarDialog}
                onClose={() => {
                    setOpenAddAvatarDialog(false);
                }}
            >
                <AddNewAvatar
                    avatarCategory={avatarCategory}
                    refetch={getAvatarListFunction}
                    setOpenDialog={setOpenAddAvatarDialog}
                />
            </Dialog>

            {dataAvatar?.id && (
                <>
                    <Dialog
                        open={openAvatarImgDialog}
                        onClose={() => {
                            setOpenAvatarImgDialog(false);
                        }}
                    >
                        <AddImages
                            imageUrl={
                                dataAvatar?.haveFile
                                    ? [
                                          `${API_ENDPOINT_URL}/avatar/file/${dataAvatar.id}`,
                                      ]
                                    : []
                            }
                            setOpenImgDialog={setOpenAvatarImgDialog}
                            onSubmitFun={updateImages}
                            dialogTitle={'AVATAR IMAGE'}
                        />
                    </Dialog>

                    <Dialog
                        open={openEditAvatarDialog}
                        onClose={() => {
                            setOpenEditAvatarDialog(false);
                        }}
                    >
                        <EditAvatar
                            refetch={getAvatarListFunction}
                            avatarId={dataAvatar.id}
                            setOpenEditAvatarDialog={setOpenEditAvatarDialog}
                        />
                    </Dialog>
                </>
            )}
            <SuccessSnackbarEffect
                isSuccess={imagesIsSuccess}
                message={'Successfully Created an Avatar Images!'}
            />
        </Box>
    );
};

export default AvatarSystems;
